import { TarifaReferenciaEnum } from '@shared/models/tarifa-referencia.models';

/**
 * Models para Jornada Offline — Proposta Editar
 * Contratos de request/response para API api-carsubs-locavia-web
 */

// ========================================
// Shared Config / Enums
// ========================================

/** Configuração de campo obrigatório vinda de IntegracoesCarsubsService */
export interface CampoObrigatorioConfig {
  Aba?: string;
  Campo: string;
  Obrigatorio: 'S' | 'N';
}

/** Situações relevantes da proposta (evita magic numbers) */
export enum PropostaSituacao {
  JornadaOffline = 1,
}

export enum PropostaSubSituacao {
  AguardandoPreenchimento = 4,
  EnviadaCliente = 5,
}

function normalizarCodigoStatus(
  codigo: number | string | null | undefined
): number | null {
  if (codigo === null || codigo === undefined || codigo === '') {
    return null;
  }

  const numero = Number(codigo);
  return Number.isNaN(numero) ? null : numero;
}

export function isPropostaEmEdicao(
  codigoSituacao: number | string | null | undefined,
  codigoSubSituacao: number | string | null | undefined
): boolean {
  return (
    normalizarCodigoStatus(codigoSituacao) === PropostaSituacao.JornadaOffline &&
    normalizarCodigoStatus(codigoSubSituacao) === PropostaSubSituacao.AguardandoPreenchimento
  );
}

export function isPropostaEnviadaCliente(
  codigoSituacao: number | string | null | undefined,
  codigoSubSituacao: number | string | null | undefined
): boolean {
  return (
    normalizarCodigoStatus(codigoSituacao) === PropostaSituacao.JornadaOffline &&
    normalizarCodigoStatus(codigoSubSituacao) === PropostaSubSituacao.EnviadaCliente
  );
}

export function getJornadaOfflineStatusLabel(
  codigoSituacao: number | string | null | undefined,
  codigoSubSituacao: number | string | null | undefined
): string | null {
  if (isPropostaEmEdicao(codigoSituacao, codigoSubSituacao)) {
    return 'Aguardando preenchimento';
  }

  if (isPropostaEnviadaCliente(codigoSituacao, codigoSubSituacao)) {
    return 'Enviada pelo cliente';
  }

  return null;
}

// ========================================
// GET /proposta/:id Response
// ========================================

export interface PropostaItem {
  DescricaoModelo: string;
  DescricaoCor: string;
  ValorNominal: number;
  PrazoProposta: number;
  DescricaoGrupo: string;
  UrlVeiculoModeloCor?: string;
  CodigoHexadecimal?: string;
  FranquiaKmMensal?: number;
  ReferenciaTarifa?: TarifaReferenciaEnum | null;
  FinalPlaca?: string | null;
}

export interface Endereco {
  CodigoEndereco: number;
  Cep: string;
  Logradouro: string;
  Numero: number;
  Complemento: string;
  Bairro: string;
  CodigoMunicipio: number;
  NomeMunicipio: string;
  SiglaEstado: string;
  NomeEstado: string;
  CodigoPais: number;
  NomePais: string;
}

export interface Pessoa {
  TipoPessoa: string;
  RazaoSocial: string;
  NomeFantasia: string;
  CPF: string;
  Email: string;
  Telefone: string;
  RG: string | null;
  OrgaoEmissorRG?: string | null;
  DataEmissaoRG?: string | null;
  DataNascimento: string | null;
  EstadoCivil?: string | null;
  Enderecos: Endereco[];
}

export interface Condutor {
  CodigoCondutor: number;
  NomeMae?: string | null;
  CodigoCategoriaCNH: number | null;
  DescricaoCategoriaCNH: string | null;
  NumeroCNH: string | null;
  NumeroRegistroCNH: string | null;
  NumeroSegurancaCNH: string | null;
  OrgaoEmissorCNH: string | null;
  CodigoMunicipioEmissorCNH?: number | null;
  DataEmissaoCNH: string | null;
  DataValidadeCNH: string | null;
  DataPrimeiraHabilitacao: string | null;
}

export interface Cliente {
  CodigoCliente: number;
  Pessoa: Pessoa;
  Condutor: Condutor | null;
  AtividadesProfissional: string | null;
  SalarioTrabalho: number | null;
  NumeroCartao: string | null; // masked para display
  NomeCartaoCarSubs: string | null;
  ValidadeCartao: string | null;
  CodigoAdministradoraCartao: number | null;
}

export interface SecoesCompletas {
  DadosPessoais: boolean;
  Endereco: boolean;
  CNH: boolean;
  Pagamento: boolean;
  Anexos: boolean;
}

export interface GetPropostaResponse {
  status: boolean;
  statusCode: number;
  erros: string[];
  data: {
    CodigoProposta: number;
    NumeroProposta: number;
    CodigoPropostaSituacao: number | string; // Backend pode retornar como string
    CodigoPropostaSubSituacao?: number | string | null;
    CodigoRevisao?: number | string;
    Situacao?: string;
    OrigemProposta: string;
    DataAbertura?: string;
    Validade: string;
    PropostaItem: PropostaItem | null;
    Cliente: {
      CodigoCliente: number;
      CodigoPessoa: number;
      Situacao: string;
    } | null;
    // Estrutura flat que vem do backend (fora de Cliente)
    Pessoa?: Pessoa | null;
    Endereco?: Endereco | null;
    Condutor?: Condutor | null;
    DadosProfissionais?: {
      Profissao: string;
      RendaMensal: number;
    } | null;
    Pagamento?: {
      NumeroCartao: string | null;
      NomeCartaoCarSubs: string | null;
      ValidadeCartao: string | null;
      CodigoAdministradoraCartao: number | null;
      DescricaoAdministradoraCartao: string | null;
    } | null;
    SecoesCompletas: SecoesCompletas;
    CategoriasCNH?: { CodigoCategoria: number; Sigla: string }[];
    Anexos?: {
      CodigoAnexo: number;
      Campo: string;
      NomeArquivo: string;
    }[];
  };
}

// ========================================
// PUT /proposta/:id Request/Response
// ========================================

export interface UpdatePessoaRequest {
  RazaoSocial?: string;
  NomeFantasia?: string;
  RG?: string;
  OrgaoEmissorRG?: string;
  DataEmissaoRG?: string;
  DataNascimento?: string;
  Email?: string;
  Telefone?: string;
  EstadoCivil?: string;
}

export interface UpdateEnderecoRequest {
  CodigoEndereco?: number;
  Cep: string;
  Logradouro: string;
  Numero: number;
  Complemento?: string;
  Bairro: string;
  CodigoMunicipio: number;
  CodigoPais?: number;
}

export interface UpdateCondutorRequest {
  NomeMae?: string;
  CodigoCategoriaCNH?: number;
  NumeroCNH?: string;
  NumeroRegistroCNH?: string;
  NumeroSegurancaCNH?: string;
  OrgaoEmissorCNH?: string;
  CodigoMunicipioEmissorCNH?: number;
  DataEmissaoCNH?: string;
  DataValidadeCNH?: string;
  DataPrimeiraHabilitacao?: string;
}

export interface UpdatePagamentoRequest {
  NumeroCartao?: string;
  NomeCartaoCarSubs?: string;
  ValidadeCartao?: string;
  CodigoAdministradoraCartao?: number;
}

export interface UpdateDadosProfissionaisRequest {
  AtividadesProfissional?: string;
  SalarioTrabalho?: number;
}

export interface UpdatePropostaClienteRequest {
  Pessoa?: UpdatePessoaRequest;
  Endereco?: UpdateEnderecoRequest;
  Condutor?: UpdateCondutorRequest;
  Pagamento?: UpdatePagamentoRequest;
  DadosProfissionais?: UpdateDadosProfissionaisRequest;
}

export interface UpdatePropostaClienteResponse {
  status: boolean;
  statusCode: number;
  erros: string[];
  data: {
    CodigoProposta: number;
    SecoesCompletas: SecoesCompletas;
  };
}

// ========================================
// POST /proposta/:id/confirmar Request/Response
// ========================================

export interface ConfirmarPropostaRequest {
  AceitouTermos?: boolean;
}

// ========================================
// POST /proposta/:id/confirmar Response
// ========================================

export interface ConfirmarPropostaResponse {
  status: boolean;
  statusCode: number;
  erros: string[];
  data: {
    CodigoProposta: number;
    NovaSituacao: string;
    Mensagem: string;
  };
}

export interface GetPropostaStatusResponse {
  status: boolean;
  statusCode: number;
  erros: string[];
  data: {
    CodigoProposta: number;
    CodigoPropostaSituacao: number;
    CodigoPropostaSubSituacao?: number | string | null;
    Situacao: string;
  };
}

export interface ServicoAdicionalItem {
  CodigoServicoAdicional: number;
  NomeServico: string;
  ValorServicoAdicional: number;
  Quantidade: number;
  ValorUnitario: number;
  Flexivel: string;
}

export interface GetServicosAdicionaisResponse {
  status: boolean;
  statusCode: number;
  erros: string[];
  data: ServicoAdicionalItem[];
}

// ========================================
// ViaCEP Response
// ========================================

export interface ViaCepResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  ibge: string;
  gia: string;
  ddd: string;
  siafi: string;
  erro?: boolean;
}

// ========================================
// Dropdown Options
// ========================================

export interface CategoriaCNH {
  codigo: number;
  descricao: string;
}

export const CATEGORIAS_CNH: CategoriaCNH[] = [
  { codigo: 1, descricao: 'A' },
  { codigo: 2, descricao: 'B' },
  { codigo: 3, descricao: 'C' },
  { codigo: 4, descricao: 'D' },
  { codigo: 5, descricao: 'E' },
  { codigo: 6, descricao: 'AB' },
  { codigo: 7, descricao: 'AC' },
  { codigo: 8, descricao: 'AD' },
  { codigo: 9, descricao: 'AE' },
];

export interface Estado {
  sigla: string;
  nome: string;
}

export const ESTADOS_BR: Estado[] = [
  { sigla: 'AC', nome: 'Acre' },
  { sigla: 'AL', nome: 'Alagoas' },
  { sigla: 'AP', nome: 'Amapá' },
  { sigla: 'AM', nome: 'Amazonas' },
  { sigla: 'BA', nome: 'Bahia' },
  { sigla: 'CE', nome: 'Ceará' },
  { sigla: 'DF', nome: 'Distrito Federal' },
  { sigla: 'ES', nome: 'Espírito Santo' },
  { sigla: 'GO', nome: 'Goiás' },
  { sigla: 'MA', nome: 'Maranhão' },
  { sigla: 'MT', nome: 'Mato Grosso' },
  { sigla: 'MS', nome: 'Mato Grosso do Sul' },
  { sigla: 'MG', nome: 'Minas Gerais' },
  { sigla: 'PA', nome: 'Pará' },
  { sigla: 'PB', nome: 'Paraíba' },
  { sigla: 'PR', nome: 'Paraná' },
  { sigla: 'PE', nome: 'Pernambuco' },
  { sigla: 'PI', nome: 'Piauí' },
  { sigla: 'RJ', nome: 'Rio de Janeiro' },
  { sigla: 'RN', nome: 'Rio Grande do Norte' },
  { sigla: 'RS', nome: 'Rio Grande do Sul' },
  { sigla: 'RO', nome: 'Rondônia' },
  { sigla: 'RR', nome: 'Roraima' },
  { sigla: 'SC', nome: 'Santa Catarina' },
  { sigla: 'SP', nome: 'São Paulo' },
  { sigla: 'SE', nome: 'Sergipe' },
  { sigla: 'TO', nome: 'Tocantins' },
];
